import { useEffect, useMemo, useState } from 'react'
import { Save, X } from 'lucide-react'
import { Button } from '../ui/button'
import BackLink from '../BackLink'
import PlayerTypeahead from '../PlayerTypeahead'
import { useNotification } from '../../contexts/NotificationContext'
import { fetchBracketWithDetails, savePoolBracketData } from '../../services/bracketService'

const POOL_SIZES = [4, 5, 6]
const POOLS = [
  { id: 'A', label: 'Pool A' },
  { id: 'B', label: 'Pool B' },
]

function makeEmptyRoster(size, teamSize) {
  return Array.from({ length: size }, () =>
    teamSize > 1 ? Array.from({ length: teamSize }, () => null) : null
  )
}

// Rebuild a pool's roster array, placing each saved team at its pool_seed
// slot (1-based) so partial saves restore exactly where they were left —
// gaps included. Sized to the persisted pool size (falls back to the
// highest occupied slot when size is unknown).
function rosterFromBracket(pool, teamSize, poolSize) {
  if (!pool) return null
  const maxSeed = pool.teams.reduce((m, t) => Math.max(m, t.pool_seed || 0), 0)
  const size = Math.max(poolSize || 0, maxSeed)
  if (size === 0) return null
  const roster = Array.from({ length: size }, () =>
    teamSize > 1 ? Array.from({ length: teamSize }, () => null) : null
  )
  pool.teams.forEach(team => {
    const idx = (team.pool_seed || 1) - 1
    if (idx < 0 || idx >= size) return
    if (teamSize > 1) {
      const ids = team.player_ids || []
      const players = team.players || []
      roster[idx] = Array.from({ length: teamSize }, (_, i) =>
        players.find(p => p.id === ids[i]) || null
      )
    } else {
      roster[idx] = team.players?.[0] || null
    }
  })
  return roster
}

export default function PoolPlaySetup({ bracketId }) {
  const notify = useNotification()
  const [bracket, setBracket] = useState(null)
  const [tournament, setTournament] = useState(null)
  const [division, setDivision] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [poolASize, setPoolASize] = useState(5)
  const [poolBSize, setPoolBSize] = useState(5)
  const [poolARoster, setPoolARoster] = useState(() => makeEmptyRoster(5, 1))
  const [poolBRoster, setPoolBRoster] = useState(() => makeEmptyRoster(5, 1))

  // Pool-play brackets created post-DUB-116 have no division: each pool slot
  // holds a single named team entry. Legacy brackets that were bolted onto a
  // Men's-Doubles-style division still use that division's team_size + gender.
  const isDecoupledPoolPlay = !division
  const teamSize = isDecoupledPoolPlay ? 1 : (division?.team_size || 1)
  const isTeam = teamSize > 1
  const isMixed = isTeam && division?.gender === 'mixed'
  const sportId = tournament?.sport_id || null
  const slotPlaceholder = isDecoupledPoolPlay ? 'Enter team name…' : 'Enter player name…'

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      try {
        const data = await fetchBracketWithDetails(bracketId)
        if (cancelled) return
        setBracket(data)
        setTournament(data?.tournament || null)
        setDivision(data?.division || null)

        const ts = data?.division?.team_size || 1
        const poolA = (data?.pool_rosters || []).find(p => p.pool_id === 'A')
        const poolB = (data?.pool_rosters || []).find(p => p.pool_id === 'B')
        // Sizes come from the persisted per-pool selection (set on save).
        // Fall back to 5 only for a never-saved bracket.
        const aSize = data?.pool_a_size || 5
        const bSize = data?.pool_b_size || 5
        const aRoster = rosterFromBracket(poolA, ts, aSize)
        const bRoster = rosterFromBracket(poolB, ts, bSize)
        setPoolASize(aSize)
        setPoolARoster(aRoster || makeEmptyRoster(aSize, ts))
        setPoolBSize(bSize)
        setPoolBRoster(bRoster || makeEmptyRoster(bSize, ts))
      } catch (err) {
        console.error('Failed to load pool bracket:', err)
        notify.error('Failed to load bracket')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    if (bracketId) load()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bracketId])

  const handleSizeChange = (poolId, newSize) => {
    if (poolId === 'A') {
      setPoolASize(newSize)
      setPoolARoster(prev => resizeRoster(prev, newSize, teamSize))
    } else {
      setPoolBSize(newSize)
      setPoolBRoster(prev => resizeRoster(prev, newSize, teamSize))
    }
  }

  const handleRosterChange = (poolId, slotIdx, memberIdx, value) => {
    const setter = poolId === 'A' ? setPoolARoster : setPoolBRoster
    setter(prev => {
      const next = prev.map(e => Array.isArray(e) ? [...e] : e)
      if (isTeam) {
        if (!Array.isArray(next[slotIdx])) {
          next[slotIdx] = Array.from({ length: teamSize }, () => null)
        }
        next[slotIdx][memberIdx] = value
      } else {
        next[slotIdx] = value
      }
      return next
    })
  }

  const allUsedPlayerIds = useMemo(() => {
    const ids = []
    const pushFromRoster = (r) => {
      r.forEach(entry => {
        if (Array.isArray(entry)) entry.forEach(p => p?.id && ids.push(p.id))
        else if (entry?.id) ids.push(entry.id)
      })
    }
    pushFromRoster(poolARoster)
    pushFromRoster(poolBRoster)
    return ids
  }, [poolARoster, poolBRoster])

  // DUB-135/136: build a payload that allows PARTIAL pools. Empty slots are
  // skipped (saved as work-in-progress); a filled slot keeps its index as
  // pool_seed so it reloads in place. Only a half-filled team slot (legacy
  // team mode) is an error — an empty slot is just "not done yet". The size
  // is persisted so the open-for-picks guard knows the target.
  const buildPayload = () => {
    const buildPool = (poolId, roster, size) => {
      const teams = []
      roster.forEach((entry, idx) => {
        if (isTeam) {
          const arr = Array.isArray(entry) ? entry : []
          const filled = arr.filter(p => p?.id)
          if (filled.length === 0) return
          if (filled.length !== teamSize) {
            throw new Error(`Pool ${poolId} slot ${idx + 1}: fill all ${teamSize} players or clear the slot`)
          }
          teams.push({ player_ids: arr.map(p => p.id), pool_seed: idx + 1 })
        } else {
          if (!entry?.id) return
          teams.push({ player_ids: [entry.id], pool_seed: idx + 1 })
        }
      })
      return { pool_id: poolId, size, teams }
    }
    return [
      buildPool('A', poolARoster, poolASize),
      buildPool('B', poolBRoster, poolBSize),
    ]
  }

  const handleSave = async () => {
    let payload
    try {
      payload = buildPayload()
    } catch (err) {
      notify.error(err.message)
      return
    }

    setSaving(true)
    try {
      const result = await savePoolBracketData(bracketId, payload, false)
      notify.success(`Saved ${result.pool_matches_inserted} pool matches + ${result.sunday_matches_inserted} Finals slots.`)
    } catch (err) {
      console.error('Failed to save pool bracket:', err)
      notify.error(err.message || 'Failed to save bracket')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-4">
      {tournament?.id && (
        <BackLink
          label={tournament.name}
          to={`/admin/tournaments/${tournament.id}/edit`}
        />
      )}

      <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <div>
          <h1 className="text-2xl font-bold">{tournament?.name || 'Tournament'}</h1>
          <p className="text-muted-foreground text-sm">
            {isDecoupledPoolPlay ? 'Pool Play setup' : `${division.name} — Pool Play setup`}
          </p>
        </div>
        <Button variant="primaryNeon" onClick={handleSave} disabled={saving} className="w-full sm:w-auto">
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saving…' : 'Save bracket'}
        </Button>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {POOLS.map(({ id, label }) => {
          const size = id === 'A' ? poolASize : poolBSize
          const roster = id === 'A' ? poolARoster : poolBRoster
          const matchCount = (size * (size - 1)) / 2

          return (
            <section key={id} className="rounded-lg border bg-background p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold">{label}</h2>
                <div className="flex items-center gap-2 text-sm">
                  <label className="text-muted-foreground">Teams:</label>
                  <select
                    value={size}
                    onChange={e => handleSizeChange(id, parseInt(e.target.value, 10))}
                    className="border rounded px-2 py-1 bg-background"
                    disabled={saving}
                  >
                    {POOL_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>

              <p className="text-xs text-muted-foreground">
                Round-robin: {matchCount} matches
              </p>

              <ul className="space-y-2">
                {roster.map((entry, idx) => {
                  const otherIds = allUsedPlayerIds.filter(pid => {
                    if (isTeam) {
                      const arr = Array.isArray(entry) ? entry : []
                      return !arr.some(p => p?.id === pid)
                    }
                    return entry?.id !== pid
                  })
                  // DUB-128: a slot is clearable when it holds any selection.
                  const slotHasValue = isTeam
                    ? Array.isArray(entry) && entry.some(p => p?.id)
                    : !!entry?.id
                  const clearSlot = () => {
                    if (isTeam) {
                      for (let mi = 0; mi < teamSize; mi++) {
                        handleRosterChange(id, idx, mi, null)
                      }
                    } else {
                      handleRosterChange(id, idx, 0, null)
                    }
                  }
                  return (
                    <li key={idx} className="flex items-start gap-3 rounded-md border p-2">
                      {/* DUB-133: no seed/slot numbers in pool-play UI at all
                          (owner directive 2026-05-20). Internal slot order is
                          preserved by render order; numeric label removed. */}
                      {isTeam ? (
                        <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {Array.from({ length: teamSize }).map((_, mi) => {
                            const lbl = isMixed ? (mi === 0 ? 'Female' : 'Male') : `Player ${mi + 1}`
                            const pos = isMixed ? mi + 1 : null
                            const member = Array.isArray(entry) ? entry[mi] : null
                            return (
                              <div key={mi}>
                                <label className="text-xs text-muted-foreground mb-1 block">{lbl}</label>
                                <PlayerTypeahead
                                  value={member}
                                  onChange={v => handleRosterChange(id, idx, mi, v)}
                                  placeholder={slotPlaceholder}
                                  divisionGender={division?.gender || null}
                                  divisionTeamSize={teamSize}
                                  playerPosition={pos}
                                  excludePlayerIds={otherIds}
                                  sportId={sportId}
                                />
                              </div>
                            )
                          })}
                        </div>
                      ) : (
                        <div className="flex-1">
                          <PlayerTypeahead
                            value={entry}
                            onChange={v => handleRosterChange(id, idx, 0, v)}
                            placeholder={slotPlaceholder}
                            divisionGender={division?.gender || null}
                            divisionTeamSize={1}
                            excludePlayerIds={otherIds}
                            sportId={sportId}
                          />
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={clearSlot}
                        disabled={!slotHasValue || saving}
                        aria-label={`Clear slot ${idx + 1}`}
                        title="Clear slot"
                        // Owner 2026-05-20: pressing Enter on the typeahead
                        // input was jumping focus to this X button (the next
                        // focusable sibling). tabIndex=-1 keeps the button
                        // mouse-clickable but skips it in the Enter/Tab
                        // chain, so focus flows to the next slot's input.
                        tabIndex={-1}
                        className="mt-1 h-7 w-7 shrink-0 inline-flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/60 disabled:opacity-30 disabled:hover:bg-transparent"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </li>
                  )
                })}
              </ul>

              {/* DUB-132: surface the round-robin pairings once 2+ slots have
                  a team — admins want the same matchup visibility single-elim
                  setup gives them. Renders names when filled, slot index
                  otherwise (so the order is clear before everything's filled). */}
              <PoolMatchupsList roster={roster} teamSize={teamSize} isTeam={isTeam} />
            </section>
          )
        })}
      </div>

      <section className="rounded-lg border bg-muted/30 p-4">
        <h2 className="text-lg font-semibold mb-2">Finals matchups</h2>
        <p className="text-xs text-muted-foreground mb-3">
          Finals participants are derived from each fan's projected pool standings: A1 vs B1, A2 vs B2, A3 vs B3, A4 vs B4. Nothing to configure here.
        </p>
        <ol className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
          {[1, 2, 3, 4].map(i => (
            <li key={i} className="rounded border bg-background px-3 py-2 text-center">
              <div className="text-xs text-muted-foreground">Match {i}</div>
              <div className="font-mono">A{i} vs B{i}</div>
            </li>
          ))}
        </ol>
      </section>
    </div>
  )
}

function resizeRoster(prev, newSize, teamSize) {
  const next = prev.slice(0, newSize)
  while (next.length < newSize) {
    next.push(teamSize > 1 ? Array.from({ length: teamSize }, () => null) : null)
  }
  return next
}

// DUB-132: render the round-robin pairings for a pool. Each cell shows the
// team label (the team name when filled, otherwise the slot index) — so the
// matchup grid is visible even before every slot is assigned.
function PoolMatchupsList({ roster, teamSize, isTeam }) {
  const labelFor = (idx) => {
    const entry = roster[idx]
    if (isTeam) {
      const arr = Array.isArray(entry) ? entry : []
      const names = arr.filter(p => p?.name).map(p => p.name)
      return names.length ? names.join(' / ') : `Slot ${idx + 1}`
    }
    return entry?.name || `Slot ${idx + 1}`
  }

  const matchups = []
  for (let i = 0; i < roster.length; i++) {
    for (let j = i + 1; j < roster.length; j++) {
      matchups.push({ key: `${i}-${j}`, a: labelFor(i), b: labelFor(j) })
    }
  }
  if (matchups.length === 0) return null

  return (
    <div className="rounded-md border bg-muted/30 overflow-hidden mt-1">
      <div className="px-3 py-1.5 border-b bg-muted/40 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Round-robin matchups
      </div>
      <ol className="text-sm">
        {/* DUB-133: no matchup-index numbers — pool play has no seeding. */}
        {matchups.map(m => (
          <li
            key={m.key}
            className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 px-3 py-1.5 border-t first:border-t-0"
          >
            <span className="truncate text-right">{m.a}</span>
            <span className="text-xs text-muted-foreground">vs</span>
            <span className="truncate">{m.b}</span>
          </li>
        ))}
      </ol>
    </div>
  )
}
